<template>
  <div
    class="rank-timeline"
    :class="{ 'is-redirect': redirectOnClick }"
    @click="handleAreaClick"
  >
    <div class="timeline-track">
      <div class="timeline-line">
        <div
          class="timeline-line-filled"
          :style="{ width: filledLineWidth }"
        ></div>
      </div>
      <div
        v-for="(rank, index) in ranks"
        :key="index"
        class="rank-node"
        :class="{
          'is-current': currentRankIndex === index,
          'is-active': activeRankIndex === index,
          'is-completed': index < currentRankIndex,
          'no-hover': redirectOnClick
        }"
        @click.stop="handleNodeClick(rank)"
      >
        <div
          class="rank-node-circle"
          :style="
            rank.isReached && rank.color ? { borderColor: rank.color } : null
          "
        >
          <img v-if="rank.image" :src="rank.image" :alt="rank.name" />
          <div v-else class="rank-img-placeholder">
            {{ rank.shortName[0] }}
          </div>
        </div>
        <div v-if="rank.isClaimed" class="rank-status-badge is-claimed">✓</div>
        <div v-else-if="rank.isReached" class="rank-status-badge is-reached">
          !
        </div>
        <div class="rank-node-label text-shadow">{{ rank.shortName }}</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RankingTimeline',
  props: {
    ranks: {
      type: Array,
      default: () => []
    },
    currentRankIndex: {
      type: Number,
      default: 0
    },
    activeRankIndex: {
      type: Number,
      default: 0
    },
    redirectOnClick: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    filledLineWidth() {
      if (this.ranks.length <= 1 || this.currentRankIndex === 0) return '0%'
      const pct = (this.currentRankIndex / (this.ranks.length - 1)) * 100
      return `${pct}%`
    }
  },
  methods: {
    handleAreaClick() {
      if (this.redirectOnClick) {
        this.$emit('area-click')
      }
    },
    handleNodeClick(rank) {
      if (!this.redirectOnClick) {
        this.$emit('node-click', rank)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.rank-timeline {
  margin: 20px 0 10px;

  &.is-redirect {
    cursor: pointer;
  }

  .text-shadow {
    text-shadow: -1px -1px 0px #000, 0px -1px 0px #000, 1px -1px 0px #000,
      -1px 0px 0px #000, 1px 0px 0px #000, -1px 1px 0px #000, 0px 1px 0px #000,
      1px 1px 0px #000;
  }

  .timeline-track {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 10px 30px;

    .timeline-line {
      position: absolute;
      top: calc(clamp(32px, 8.5vw, 60px) / 2 + 10px);
      left: calc(clamp(32px, 8.5vw, 60px) / 2 + 30px);
      right: calc(clamp(32px, 8.5vw, 60px) / 2 + 30px);
      height: 3px;
      background: rgba($text-color, 0.25);
      z-index: 0;

      .timeline-line-filled {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        background: rgba($success, 0.8);
        transition: width 0.4s ease;
      }
    }
  }

  .rank-node {
    position: relative;
    z-index: 2;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;

    &.no-hover {
      cursor: default;
      pointer-events: none;
    }

    // Node size scales with viewport: min 32px (7 nodes on ~320px), max 60px
    --node-size: clamp(32px, 10.5vw, 60px);

    .rank-node-circle {
      position: relative;
      width: var(--node-size);
      height: var(--node-size);
      border-radius: 50%;
      background: rgba(white, 1);
      border: 3px solid transparent;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: visible;
      transform: scale(1);
      transition: transform 0.2s ease;

      img {
        max-width: 100%;
        max-height: 100%;
        width: auto;
        height: auto;
        object-fit: contain;
        border-radius: 50%;
        filter: drop-shadow(0 0px 4px rgba(0, 0, 0, 0.5));
      }

      .rank-img-placeholder {
        font-size: clamp(0.6rem, 2vw, 1.2rem);
        font-weight: 700;
        color: rgba($text-color, 0.6);
      }
    }

    .rank-status-badge {
      position: absolute;
      bottom: calc(var(--node-size) * 0.3);
      right: calc(var(--node-size) * -0.1);
      width: clamp(14px, 3.5vw, 22px);
      height: clamp(14px, 3.5vw, 22px);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: clamp(0.45rem, 1.2vw, 0.7rem);
      font-weight: 900;
      line-height: 1;
      border: 2px solid #111;
      z-index: 3;
      color: #fff;

      &.is-claimed {
        background: $success;
      }

      &.is-reached {
        background: $warning;
        color: #111;
      }
    }

    .rank-node-label {
      font-size: clamp(0.4rem, 1.2vw, 0.6rem);
      color: #ffffff;
      text-align: center;
      white-space: nowrap;
    }

    &.is-active .rank-node-circle {
      &::before {
        content: '';
        position: absolute;
        inset: -5px;
        border-radius: 50%;
        border: 2px solid rgba($success, 0.5);
        box-shadow: 0 0 5px rgba($success, 0.5),
          inset 0 0 6px rgba($success, 0.3);
        pointer-events: none;
      }
    }

    &.is-current .rank-node-circle {
      box-shadow: 0 0 14px rgba($success, 0.8);
    }

    &.is-completed .rank-node-circle {
      border: 3px solid $primary;
    }

    &:not(.no-hover):hover .rank-node-circle {
      transform: scale(1.1);
    }
  }
}
</style>
